import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { SectionHeader } from "./SectionHeader";
import { useFilters } from "../context/FilterContext";
import { buildDashboardParams } from "../utils/dashboardFilters";

/**
 * Sanctioned vs Disbursed trend chart.
 * Two toggles on the section header:
 *   - Metric   : Sanctioned | Disbursed  (default = Disbursed)
 *   - Cadence  : Quarterly  | Yearly     (default = Yearly)
 */
export default function QuarterlyTrendChart() {
  const { filters, viewMode, API } = useFilters();
  const params = buildDashboardParams(filters, { view_mode: viewMode });
  const [quarterlyData, setQuarterlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState("disbursed");   // 'sanctioned' | 'disbursed'
  const [cadence, setCadence] = useState("yearly");    // 'quarterly'  | 'yearly'

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/executive/quarterly`, { params })
      .then(r => setQuarterlyData(Array.isArray(r.data) ? r.data : []))
      .catch(() => setQuarterlyData([]))
      .finally(() => setLoading(false));
  }, [API, JSON.stringify(params)]);  // eslint-disable-line react-hooks/exhaustive-deps

  const isSanctioned = metric === "sanctioned";
  const isYearly     = cadence === "yearly";
  const dataKey      = isSanctioned ? "sanctioned_cr" : "disbursed_cr";
  const lineColor    = isSanctioned ? "#B71C1C" : "#1E40AF";
  const metricLabel  = isSanctioned ? "Sanctioned" : "Disbursed";

  // Aggregate quarters into yearly buckets when Yearly is active
  const chartData = useMemo(() => {
    if (!isYearly) return quarterlyData;
    const byFy = new Map();
    for (const row of quarterlyData) {
      const key = row.fy;
      if (!byFy.has(key)) {
        const start = row.fy?.split("-")[0]?.slice(-2) ?? "";
        const end   = row.fy?.split("-")[1]?.slice(-2) ?? "";
        byFy.set(key, { label: `FY${start}-${end}`, fy: row.fy, sanctioned_cr: 0, disbursed_cr: 0 });
      }
      const agg = byFy.get(key);
      agg.sanctioned_cr += Number(row.sanctioned_cr || 0);
      agg.disbursed_cr  += Number(row.disbursed_cr  || 0);
    }
    return Array.from(byFy.values()).map(r => ({
      ...r,
      sanctioned_cr: Math.round(r.sanctioned_cr * 100) / 100,
      disbursed_cr:  Math.round(r.disbursed_cr  * 100) / 100,
    }));
  }, [quarterlyData, isYearly]);

  return (
    <SectionHeader
      title={`${isYearly ? "Yearly" : "Quarterly"} Sanctioned vs Disbursed`}
      subtitle={`${isYearly ? "Year-wise" : "Quarter-wise"} sanctioned & disbursement trend across all FYs`}
      collapsible
      defaultOpen={false}
      headerRight={
        <div className="flex items-center gap-3">
          {/* Cadence toggle: Quarterly | Yearly */}
          <div className="flex items-center bg-slate-100 rounded-full p-0.5" data-testid="qoq-cadence-toggle">
            <button
              type="button"
              onClick={() => setCadence("yearly")}
              data-testid="qoq-cadence-yearly"
              className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-colors ${isYearly ? "bg-white text-[#B71C1C] shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
            >
              Yearly
            </button>
            <button
              type="button"
              onClick={() => setCadence("quarterly")}
              data-testid="qoq-cadence-quarterly"
              className={`text-[10px] px-2.5 py-1 rounded-full font-medium transition-colors ${!isYearly ? "bg-white text-[#B71C1C] shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
            >
              Quarterly
            </button>
          </div>

          {/* Metric toggle: Sanctioned | Disbursed */}
          <div className="flex items-center gap-1.5" data-testid="qoq-metric-toggle">
            <button
              type="button"
              onClick={() => setMetric("sanctioned")}
              data-testid="qoq-metric-sanctioned"
              className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${isSanctioned ? "bg-[#B71C1C] text-white border-[#B71C1C]" : "bg-white text-slate-600 border-slate-200 hover:border-[#B71C1C]"}`}
            >
              Sanctioned
            </button>
            <button
              type="button"
              onClick={() => setMetric("disbursed")}
              data-testid="qoq-metric-disbursed"
              className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-colors ${!isSanctioned ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-200 hover:border-blue-700"}`}
            >
              Disbursed
            </button>
          </div>
        </div>
      }
    >
      <div className="p-1" data-testid="qoq-chart-section">
        {chartData.length > 0 ? (
          <div data-testid="qoq-chart">
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} angle={isYearly ? 0 : -45} textAnchor={isYearly ? "middle" : "end"} interval={0} height={isYearly ? 40 : 60} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${v}`} width={50}
                  label={{ value: "Amount (Cr)", angle: -90, position: "insideLeft", style: { fontSize: 10, fill: "#94a3b8" }, offset: 0 }} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e2e8f0" }}
                  formatter={(value) => [`₹${value} Cr`, metricLabel]}
                  labelStyle={{ fontWeight: 600, color: "#334155" }}
                />
                <Line type="monotone" dataKey={dataKey} name={metricLabel} stroke={lineColor} strokeWidth={2.5}
                  dot={{ r: 3, fill: lineColor }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-slate-600">
            {loading ? "Loading..." : "No data"}
          </div>
        )}
      </div>
    </SectionHeader>
  );
}
